import { Component, OnInit, AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import { DatabaseService } from 'src/_services/DatabaseService';
import { ToastrManager } from 'ng6-toastr-notifications';
import { MatDialog } from '@angular/material';
import { UserVacancyFormComponent } from '../../user-vacancy/user-vacancy-form/user-vacancy-form.component';

import * as Highcharts from 'highcharts';
// import * as (not default import) — this project's tsconfig doesn't set
// esModuleInterop, and highcharts' map module is a plain CommonJS function
// export, so a default import would fail to compile.
import * as HC_map from 'highcharts/modules/map';
(HC_map as any)(Highcharts);

// Real India state boundaries — already sitting in the project, unused
// until now (Highcharts "admin1" topology format).
import * as indiaMapModule from 'src/assets/indiaMap';
const indiaMap = (indiaMapModule as any).default || indiaMapModule;

interface StateAgg {
    name: string;
    hcKey: string;
    total: number;
    vacant: number;
    filled: number;
    replacement: number;
}

@Component({
    selector: 'app-state-map',
    templateUrl: './state-map.component.html',
    styleUrls: ['./state-map.component.scss']
})
export class StateMapComponent implements OnInit, AfterViewInit {
    // Angular 7 — @ViewChild has no { static } options param yet, just the selector.
    @ViewChild('mapEl') mapEl: ElementRef;

    isLoading = false;
    private chart: Highcharts.Chart;
    private mapReady = false;
    private initialZoom: number;
    private initialCenter: [number, number];

    rawStations: any[] = [];
    rawUsers: any[] = [];

    // top stat row
    totalUsers = 0;
    activeUsers = 0;
    statesWithVacancy = 0;
    totalVacancies = 0;

    stateAgg: { [name: string]: StateAgg } = {};
    stateList: StateAgg[] = [];           // states that actually have a vacancy — for the chip list
    selected: StateAgg = null;

    constructor(public serve: DatabaseService, public toast: ToastrManager, public dialog: MatDialog) {}

    ngOnInit() {
        this.load();
    }

    ngAfterViewInit() {
        this.mapReady = true;
        if (this.rawStations.length) { this.renderMap(); }
    }

    refresh() { this.load(); }

    openAddForm() {
        const ref = this.dialog.open(UserVacancyFormComponent, {
            width: '560px',
            data: { mode: 'add' }
        });
        ref.afterClosed().subscribe(result => {
            if (result === 'saved') { this.refresh(); }
        });
    }

    load() {
        this.isLoading = true;
        this.serve.post_rqst({ filter: {} }, 'Master/baseStationMasterList').subscribe((result: any) => {
            this.isLoading = false;
            if (result['statusCode'] == 200) {
                this.rawStations = result['stations'] || [];
                this.rawUsers = result['users'] || [];
                this.buildAggregates();
                if (this.mapReady) { this.renderMap(); }
            } else {
                this.toast.errorToastr(result['statusMsg'] || 'Failed to load map data.');
            }
        }, () => {
            this.isLoading = false;
            this.toast.errorToastr('Network error.');
        });
    }

    // Wigwam's own state spellings don't all match the map's hc-key values
    // (e.g. it sends "ORISSA" but the topology's region is "odisha"; it sends
    // "JAMMU" and "KASHMIR" as two separate values but the topology only has
    // one combined "jammu and kashmir" region). This is purely a map-lookup
    // concern — state_name itself stays exactly as Wigwam sends it everywhere
    // else, so a live sync never fights this.
    private resolveHcKey(name: string): string {
        const overrides: { [k: string]: string } = {
            'ORISSA': 'odisha',
            'JAMMU': 'jammu and kashmir',
            'KASHMIR': 'jammu and kashmir',
            'JAMMU AND KASHMIR': 'jammu and kashmir',
            'DELHI': 'ncr of delhi',
            'DELHI NCR': 'ncr of delhi',
            'ANDAMAN AND NICOBAR ISLANDS': 'andaman and nicobar',
            'ARUNACHAL PRADESH': 'arunanchal pradesh', // topology has this exact typo
            'DADRA AND NAGAR HAVELI AND DAMAN AND DIU': 'dadara and nagar havelli'
        };
        const upper = name.trim().toUpperCase();
        return overrides[upper] || name.trim().toLowerCase();
    }

    // ---- aggregation ----
    private buildAggregates() {
        const agg: { [name: string]: StateAgg } = {};
        for (const s of this.rawStations) {
            const name = (s.state_name || '').trim();
            if (!name) { continue; }
            if (!agg[name]) {
                agg[name] = { name, hcKey: this.resolveHcKey(name), total: 0, vacant: 0, filled: 0, replacement: 0 };
            }
            agg[name].total++;
            if (s.status === 'OPEN') { agg[name].vacant++; }
            else { agg[name].filled++; }
            if (s.replacement_needed == 1) { agg[name].replacement++; }
        }
        this.stateAgg = agg;
        this.stateList = Object.values(agg)
            .filter(a => a.vacant > 0 || a.replacement > 0)
            .sort((a, b) => (b.vacant + b.replacement) - (a.vacant + a.replacement));

        this.totalUsers = this.rawUsers.length;
        this.activeUsers = this.rawUsers.filter((u: any) => u.status == 1).length;
        this.statesWithVacancy = this.stateList.length;
        this.totalVacancies = this.rawStations.filter((s: any) => s.status === 'OPEN').length;

        // land on the state with the most open need by default, else first state seen
        const allStates = Object.values(agg).sort((a, b) => (b.vacant + b.replacement) - (a.vacant + a.replacement));
        this.selected = allStates.length ? allStates[0] : null;
    }

    // Every station belonging to the currently-selected state — this is
    // what actually answers "which base stations in this state need
    // attention", not just the rollup counts on the panel above it.
    get selectedStations(): any[] {
        if (!this.selected) { return []; }
        return this.rawStations
            .filter((s: any) => (s.state_name || '').trim() === this.selected.name)
            .sort((a: any, b: any) => {
                // vacant/replacement first — that's what needs eyes on it
                const rank = (s: any) => s.status === 'OPEN' ? 0 : (s.replacement_needed == 1 ? 1 : 2);
                return rank(a) - rank(b);
            });
    }

    selectState(name: string, zoom: boolean = true) {
        const a = this.stateAgg[name];
        if (a) { this.selected = a; }
        if (this.chart && this.chart.series[0]) {
            const point = (this.chart.series[0].points || []).find((p: any) => (p as any).selectName === name);
            if (point) {
                (point as any).select(true, false);
                if (zoom && typeof (point as any).zoomTo === 'function') { (point as any).zoomTo(); }
            }
        }
    }

    resetMapView() {
        const mapView = this.chart && (this.chart as any).mapView;
        if (mapView && this.initialZoom !== undefined) {
            mapView.setView(this.initialCenter, this.initialZoom);
        }
    }

    // Two different state_name values can resolve to the SAME map region
    // (JAMMU + KASHMIR both land on the topology's one "jammu and kashmir"
    // region) — group by hc-key here so both contribute to that one region's
    // color/counts instead of the second silently overwriting the first.
    private buildMapSeriesData(): any[] {
        const byKey: { [hcKey: string]: StateAgg[] } = {};
        for (const a of Object.values(this.stateAgg)) {
            if (!byKey[a.hcKey]) { byKey[a.hcKey] = []; }
            byKey[a.hcKey].push(a);
        }
        return Object.entries(byKey).map(([hcKey, list]) => {
            const vacant = list.reduce((s, a) => s + a.vacant, 0);
            const filled = list.reduce((s, a) => s + a.filled, 0);
            const replacement = list.reduce((s, a) => s + a.replacement, 0);
            const displayName = list.map(a => a.name).join(' / ');
            // clicking the region opens whichever original state has the most going on
            const primary = list.slice().sort((a, b) => (b.vacant + b.replacement) - (a.vacant + a.replacement))[0];
            return {
                'hc-key': hcKey,
                name: displayName,
                value: vacant > 0 ? 2 : (replacement > 0 ? 1 : 0),
                agg: { name: displayName, vacant, filled, replacement, total: vacant + filled },
                selectName: primary.name
            };
        });
    }

    // ---- Highcharts map ----
    private renderMap() {
        if (!this.mapEl) { return; }
        if (this.chart) { this.chart.destroy(); } // refresh() calls this again — don't stack chart instances

        const seriesData = this.buildMapSeriesData();

        const self = this;

        this.chart = Highcharts.mapChart(this.mapEl.nativeElement, {
            chart: { map: indiaMap as any, backgroundColor: 'transparent' },
            title: { text: undefined },
            credits: { enabled: false },
            legend: { enabled: false },
            mapNavigation: { enabled: true, buttonOptions: { verticalAlign: 'bottom' } },
            tooltip: {
                formatter: function () {
                    const a: StateAgg = (this.point as any).agg;
                    if (!a) { return this.point.name; }
                    return `<b>${a.name}</b><br/>Vacant: ${a.vacant} &middot; Filled: ${a.filled}${a.replacement ? ' &middot; Replacement: ' + a.replacement : ''}`;
                }
            },
            colorAxis: { min: 0, max: 2, stops: [
                [0, '#5c35d4'],   // fully staffed
                [0.5, '#d17a00'], // replacement needed
                [1, '#15803d']    // vacancy open
            ]},
            series: [{
                name: 'States',
                type: 'map',
                data: seriesData,
                joinBy: 'hc-key',
                nullColor: '#eef0f6',
                borderColor: '#ffffff',
                borderWidth: 1.2,
                allowPointSelect: true,
                states: {
                    hover: { brightness: 0.08 },
                    select: { color: '#4a29b8', borderColor: '#ffffff' }
                },
                point: {
                    events: {
                        click: function () {
                            const selectName = (this as any).selectName;
                            if (selectName) { self.selectState(selectName); }
                        }
                    }
                }
            }] as any
        });

        // remember the fitted-to-India view so "Reset View" has something to return to
        const mapView = (this.chart as any).mapView;
        if (mapView) {
            this.initialZoom = mapView.zoom;
            this.initialCenter = mapView.center;
        }
    }
}
